"use client"
import {
  PortableText,
  PortableTextComponents,
  PortableTextMarkComponentProps
} from "@portabletext/react"
import type { PortableTextBlock } from "@portabletext/types"
import { useSearchParams } from "next/navigation"
import React from "react"
import { Image } from "sanity"

import { withBasePath } from "@/lib/redirects"
import { cn } from "@/lib/utils"

import IconChevronRight from "../global/icons/IconChevronRight"
import IconExternal from "../global/icons/IconExternal"
import { Button } from "../ui/button"
import ImageBox from "./ImageBox"

export function CustomPortableText({
  listClasses,
  paragraphClasses,
  value,
  replaceFrom = undefined,
  replaceTo = undefined,
  maxColumns = 1,
  externalLinkIcon = true
}: {
  paragraphClasses?: string
  value: PortableTextBlock[]
  listClasses?: string
  replaceFrom?: string
  replaceTo?: React.ReactNode
  maxColumns?: number
  externalLinkIcon?: boolean
}) {
  const searchParams = useSearchParams()
  const q64 = searchParams?.get("q64") || undefined
  const debugMessage = searchParams?.get("d") || undefined

  const base64UrlDecode = (input: string): string | undefined => {
    try {
      const padded = input
        .replace(/-/g, "+")
        .replace(/_/g, "/")
        .padEnd(Math.ceil(input.length / 4) * 4, "=")
      const binary = atob(padded)
      const bytes = Uint8Array.from(binary, (c) => c.charCodeAt(0))
      const decoder = new TextDecoder()
      return decoder.decode(bytes)
    } catch {
      return undefined
    }
  }

  const urlReplaceTo =
    (q64 ? base64UrlDecode(q64) : searchParams?.get("q") || undefined) ||
    undefined
  const urlReplaceFrom = searchParams?.get("k") || "{{token}}"

  const effectiveFrom = replaceFrom ?? urlReplaceFrom
  const effectiveTo = replaceTo ?? urlReplaceTo
  const debugMessageFrom = "{{debug}}"
  const debugMessageTo = debugMessage
    ? base64UrlDecode(debugMessage)
    : undefined

  const components: PortableTextComponents = {
    list: {
      bullet: ({ children }) => {
        const numberOfChildren = React.Children.count(children)
        const gridClasses =
          (numberOfChildren > 3 || numberOfChildren == 2) && maxColumns > 1
            ? "md:grid-cols-2"
            : numberOfChildren > 2 && maxColumns > 1
              ? "md:grid-cols-3"
              : ""
        const wrappedChildren = React.Children.map(children, (child) => {
          // Check if child is not null or undefined and is a valid React element before cloning
          if (child != null && React.isValidElement(child)) {
            return React.cloneElement(
              child,
              {},
              <span>
                {
                  (
                    child.props as {
                      children: React.ReactNode
                    }
                  )?.children
                }
              </span>
            )
          }
          return child // Return child directly if it's null, undefined, or not a valid React element
        })
        return (
          <ul
            className={`grid gap-2 lg:gap-3 my-4 ${listClasses} child-count-${numberOfChildren} ${gridClasses}`}>
            {wrappedChildren}
          </ul>
        )
      },
      number: ({ children }) => {
        const wrappedChildren = React.Children.map(children, (child) => {
          // Check if child is not null or undefined and is a valid React element before cloning
          if (child != null && React.isValidElement(child)) {
            return React.cloneElement(
              child,
              {},
              <span>
                {
                  (
                    child.props as {
                      children: React.ReactNode
                    }
                  )?.children
                }
              </span>
            )
          }
          return child // Return child directly if it's null, undefined, or not a valid React element
        })
        return (
          <ol className={`${listClasses} list-decimal`}>{wrappedChildren}</ol>
        )
      }
    },
    block: {
      normal: ({ children }) => {
        const childs = React.Children.toArray(children)
        const newChilds = childs.map((child, childIndex) => {
          if (typeof child === "string" && debugMessageTo) {
            if (child === debugMessageFrom)
              return (
                <React.Fragment key={`debug-${childIndex}`}>
                  {debugMessageTo}
                </React.Fragment>
              )
            if (child.includes(debugMessageFrom)) {
              const parts = child.split(debugMessageFrom)
              const elements: React.ReactNode[] = []
              parts.forEach((part, index) => {
                elements.push(
                  <React.Fragment key={`part-${childIndex}-${index}`}>
                    {part}
                  </React.Fragment>
                )
                if (index < parts.length - 1) {
                  elements.push(
                    <React.Fragment key={`debug-${childIndex}-${index}`}>
                      {debugMessageTo}
                    </React.Fragment>
                  )
                }
              })
              return elements
            }
          }
          if (typeof child === "string" && effectiveFrom) {
            // If we have a replacement value, perform the replacement.
            if (effectiveTo !== undefined && effectiveTo !== null) {
              if (child === effectiveFrom)
                return (
                  <React.Fragment key={`replace-${childIndex}`}>
                    {effectiveTo}
                  </React.Fragment>
                )
              if (child.includes(effectiveFrom)) {
                const parts = child.split(effectiveFrom)
                const elements: React.ReactNode[] = []
                parts.forEach((part, index) => {
                  elements.push(
                    <React.Fragment key={`part-${childIndex}-${index}`}>
                      {part}
                    </React.Fragment>
                  )
                  if (index < parts.length - 1) {
                    elements.push(
                      <React.Fragment key={`replace-${childIndex}-${index}`}>
                        {effectiveTo}
                      </React.Fragment>
                    )
                  }
                })
                return elements
              }
              return child
            }

            // If replacement value is not available yet, hide the placeholder.
            if (child === effectiveFrom)
              return <React.Fragment key={`empty-${childIndex}`} />
            if (child.includes(effectiveFrom)) {
              const parts = child.split(effectiveFrom)
              const elements: React.ReactNode[] = []
              parts.forEach((part, index) => {
                elements.push(
                  <React.Fragment key={`part-${childIndex}-${index}`}>
                    {part}
                  </React.Fragment>
                )
                if (index < parts.length - 1) {
                  elements.push(
                    <React.Fragment key={`empty-${childIndex}-${index}`} />
                  )
                }
              })
              return elements
            }
            return child
          }
          return child
        })

        return <div className={paragraphClasses}>{newChilds}</div>
      },
      preamble: ({ children }) => {
        return <div className="preamble">{children}</div>
      },
      h1: ({ children }) => {
        return <h1 className="mb-4 mt-6">{children}</h1>
      },
      h2: ({ children }) => {
        return <h2 className="mb-4 mt-6">{children}</h2>
      },
      h3: ({ children }) => {
        return <h3 className="mb-4 mt-6">{children}</h3>
      },
      h4: ({ children }) => {
        return <h4 className="mb-4 mt-6">{children}</h4>
      }
    },
    marks: {
      left: ({ children }) => <div className="text-left">{children}</div>,
      center: ({ children }) => (
        <div className="text-center w-full">{children}</div>
      ),
      right: ({ children }) => <div className="text-right">{children}</div>,
      link: ({
        children,
        value
      }: PortableTextMarkComponentProps<{
        _type: string
        href: string
      }>) => {
        if (
          children &&
          Array.isArray(children) &&
          children[0]?.props?.markKey === "button"
        ) {
          const handleClick = () => {
            window.open(value?.href, "_blank")
          }
          if (children[0]?.props?.children?.[0]?.props?.markKey === "center") {
            return (
              <p className="text-center">
                <Button
                  onClick={handleClick}
                  variant="default"
                  analyticsEventName="PortableText Button Click"
                  analyticsProps={{ href: value?.href }}>
                  {children}
                </Button>
              </p>
            )
          }

          return (
            <Button
              onClick={handleClick}
              variant="default"
              analyticsEventName="PortableText Button Click"
              analyticsProps={{ href: value?.href }}>
              {children}
            </Button>
          )
        }

        const isExternal = value?.href?.startsWith("http")
        const hasChevronIcon =
          children &&
          Array.isArray(children) &&
          children[0]?.props?.markKey === "linkWithIcon"

        return (
          <a
            className={cn(
              "inline transition underline-offset-2 hover:underline text-purple hover:text-purple-500",
              {
                "font-bold": hasChevronIcon
              }
            )}
            href={isExternal ? value?.href : withBasePath(value?.href || "")}
            target={isExternal ? "_blank" : ""}
            rel={isExternal ? "noopener noreferrer" : ""}>
            {hasChevronIcon && (
              <IconChevronRight className="-mt-1 inline ml-1" />
            )}

            {children}
            {isExternal && externalLinkIcon && (
              <IconExternal className="w-6 h-6 inline ml-1" />
            )}
          </a>
        )
      },
      "external-link": ({
        children
      }: PortableTextMarkComponentProps<{
        _type: string
        href: string
      }>) => {
        return (
          <span>
            {children}
            {/* Icon is already added in the link mark */}
            {/* <IconExternal className="ml-1 inline" /> */}
          </span>
        )
      },
      "inner-wrapper": ({ children }) => {
        return (
          <div className="inner-wrapper p-4 md:p-6 bg-purple-600 my-4 rounded-xl">
            {children}
          </div>
        )
      },
      button: ({ children /*, value*/ }) => {
        return children
      }
    },
    types: {
      // table: (
      //   {
      //     value
      //   }: {
      //     value: {
      //       rows: {
      //         cells: {
      //           children: PortableTextBlock[]
      //         }[]
      //       }[]
      //       table_wrapped: boolean
      //       first_row_thead: boolean
      //       label_placement_on_mobile: string
      //     }
      //   },
      //   children: React.ReactNode
      // ) => {
      //   const thead = value.first_row_thead ? value.rows[0] : null
      //   return (
      //     <p
      //       className={
      //         value.table_wrapped
      //           ? "p-4 md:p-6 bg-purple-600 rounded-xl w-full mt-6"
      //           : "my-4"
      //       }>
      //       <table className="w-full">
      //         {value.first_row_thead && (
      //           <thead>
      //             <tr>
      //               {value.rows[0].cells.map((cell, cellIndex) => (
      //                 <th className="text-left" key={cellIndex}>
      //                   {cell as any}
      //                 </th>
      //               ))}
      //             </tr>
      //           </thead>
      //         )}
      //         <tbody>
      //           {value.rows.map((row, index) =>
      //             value.first_row_thead && index === 0 ? null : (
      //               <tr key={index}>
      //                 {row.cells
      //                   .filter((cell) => cell)
      //                   .map((cell, cellIndex) => (
      //                     <td
      //                       className={
      //                         value.label_placement_on_mobile === "left"
      //                           ? "flex-row"
      //                           : "flex-col mb-3 md:mb-0 last:mb-0"
      //                       }
      //                       key={cellIndex}
      //                       data-th={(thead?.cells?.[cellIndex] as any) || ""}>
      //                       {cell as any}
      //                     </td>
      //                   ))}
      //               </tr>
      //             )
      //           )}
      //         </tbody>
      //       </table>
      //     </p>
      //   )
      // },
      image: ({
        value
      }: {
        value: Image & { alt?: string; caption?: string }
      }) => {
        return (
          <div className="my-6 space-y-2">
            <ImageBox
              image={value}
              alt={value.alt}
              classesWrapper="relative aspect-[16/9]"
            />
            {value?.caption && (
              <p className="font-sans text-sm text-gray-600">{value.caption}</p>
            )}
          </div>
        )
      }
    }
  }

  return <PortableText components={components} value={value} />
}
